import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { groupMeta, groupStatus, serviceStatus, timeAgo } from "@/lib/deployments";
import { EntityLogo } from "@/components/EntityLogo";
import { StatusLabel, deploymentGroupsQuery } from "./DeploymentsPage";
import { ServiceDetail, StatusPill, type Tab } from "./ServiceDeploymentsPage";

/**
 * One deployment: a card per service in it, then the selected service's
 * deploys, logs and notes. /deployments/:groupKey/:serviceId picks the
 * service; without one, the primary (the app) is shown.
 */
export function DeploymentGroupPage() {
  const { groupKey = "", serviceId } = useParams();
  const [tab, setTab] = useState<Tab>("deploys");
  const { data, isLoading } = useQuery(deploymentGroupsQuery);

  const back = (
    <Link to="/deployments" className="text-sm text-text-muted hover:text-text-heading">
      <i className="fa-solid fa-arrow-left mr-1.5 text-xs" />
      Deployments
    </Link>
  );

  if (isLoading) {
    return (
      <div>
        {back}
        <div className="py-16 text-center text-text-muted">
          <i className="fa-solid fa-circle-notch fa-spin text-xl" />
        </div>
      </div>
    );
  }

  const group = data?.groups.find((g) => g.key === groupKey);
  if (!group) {
    // Links from before services were grouped are /deployments/<service id>.
    const home = data?.groups.find((g) => g.services.some((s) => s.id === groupKey));
    if (home) return <Navigate to={`/deployments/${home.key}/${groupKey}`} replace />;
    return (
      <div>
        {back}
        <p className="mt-6 text-sm text-text-secondary">
          {data?.configured
            ? `No deployment called “${groupKey}”.`
            : "Couldn't load deployments from Render."}
        </p>
      </div>
    );
  }

  const selected = group.services.find((s) => s.id === serviceId) ?? group.services[0];
  const st = groupStatus(group.services);
  const logoUrl = group.services.find((s) => s.logoUrl)?.logoUrl ?? null;

  return (
    <div>
      {back}

      <header className="mt-2 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <EntityLogo name={group.name} url={logoUrl} size={44} fallbackIcon="fa-server" />
          <h1 className="page-title">{group.name}</h1>
          {st && <StatusPill color={st.color} label={st.label} pulse={st.inProgress} />}
        </div>
        <p className="mt-2 text-sm text-text-muted">{groupMeta(group.services)}</p>
      </header>

      {group.services.length > 1 && (
        <nav aria-label="Microservices" className="mb-6 grid gap-3 sm:grid-cols-2">
          {group.services.map((s) => {
            const sst = serviceStatus(s);
            const active = s.id === selected.id;
            return (
              <Link
                key={s.id}
                to={`/deployments/${group.key}/${s.id}`}
                aria-current={active ? "page" : undefined}
                className={`rounded-token-lg border p-4 transition-colors ${
                  active
                    ? "border-accent bg-accent-muted shadow-token-sm"
                    : "border-border bg-bg-card hover:border-accent"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted">
                      {s.role ?? "Service"}
                    </p>
                    <p className="truncate font-display font-semibold text-text-heading">{s.name}</p>
                  </div>
                  {sst && <StatusLabel {...sst} />}
                </div>
                <div className="mt-2 flex items-center justify-between gap-3 text-xs">
                  <span className="truncate text-text-secondary">
                    {s.latestDeploy?.commitId && (
                      <span className="mr-2 font-mono text-text-muted">{s.latestDeploy.commitId}</span>
                    )}
                    {s.latestDeploy ? (s.latestDeploy.commitMessage ?? "Deploy") : "No deploys yet"}
                  </span>
                  {s.latestDeploy && (
                    <span className="shrink-0 text-text-muted">
                      {timeAgo(s.latestDeploy.finishedAt ?? s.latestDeploy.createdAt)}
                    </span>
                  )}
                </div>
              </Link>
            );
          })}
        </nav>
      )}

      <ServiceDetail
        key={selected.id}
        serviceId={selected.id}
        usage={selected.usage}
        sessions={selected.sessions}
        tab={tab}
        onTabChange={setTab}
      />
    </div>
  );
}
